import { useRef } from 'react';
import { useQueryClient } from 'react-query';

import {
  CURRENCY_BALANCES,
  type SpendableCurrency,
} from 'src/components/Wallet/PriceButton';
import { reportError } from 'src/modules/logs/Sentry';
import {
  asFreezeCapReached,
  asInsufficientFunds,
  newPurchaseIdempotencyKey,
  useWallet,
  useWalletPurchase,
} from 'src/queries/wallet';
import type { StreakFreezes } from 'src/types/streak.types';
import type { PurchaseSource } from 'src/types/wallet.types';
import { queryKeys } from 'src/queries/keys';

export type BuyFreezeState = 'confirm' | 'cap' | 'poor';

export type UseBuyStreakFreezeOptions = {
  freezes: StreakFreezes;
  held: number;
  source: PurchaseSource;
};

export const useBuyStreakFreeze = ({
  freezes,
  held,
  source,
}: UseBuyStreakFreezeOptions) => {
  const queryClient = useQueryClient();
  const currency: SpendableCurrency = freezes.price_currency ?? 'BABZ';
  const price = freezes.price ?? freezes.price_babz;
  const wallet = useWallet();
  const balance = wallet.data?.[CURRENCY_BALANCES[currency]];
  const isBalanceUnavailable = wallet.isError;
  const purchase = useWalletPurchase();
  const { cap } = freezes;
  const missing = balance === undefined ? 0 : Math.max(0, price - balance);

  const state: BuyFreezeState =
    held >= cap ? 'cap' : missing > 0 ? 'poor' : 'confirm';

  const idempotencyKey = useRef<string | null>(null);

  const isRejectionExplained =
    state !== 'confirm' &&
    (asInsufficientFunds(purchase.error) !== null ||
      asFreezeCapReached(purchase.error) !== null);

  const buy = () => {
    if (state !== 'confirm') return;
    if (idempotencyKey.current === null) {
      idempotencyKey.current = newPurchaseIdempotencyKey();
    }
    purchase.mutate(
      {
        item: 'streak_freeze',
        quantity: 1,
        idempotency_key: idempotencyKey.current,
        purchase_source: source,
      },
      {
        onSuccess: () => {
          idempotencyKey.current = null;
          void queryClient.invalidateQueries(queryKeys.streak());
        },
        onError: error => {
          if (!asInsufficientFunds(error) && !asFreezeCapReached(error)) {
            reportError('Failed to buy a streak freeze', error);
          }
          void queryClient.invalidateQueries(queryKeys.streak());
          void queryClient.invalidateQueries(queryKeys.wallet());
        },
      },
    );
  };

  return {
    state,
    buy,
    price,
    currency,
    isLoading: purchase.isLoading,
    isError: purchase.isError,
    isRejectionExplained,
    isBalanceUnavailable,
  };
};
